"use client";

import { useState, useRef } from "react";
import { Input, Button } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import chooseImage from "../../../assets/icons/tswira.png";
import { axiosDoctorAuth } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";
import icon1 from "../../../assets/images/logomedilink.svg";
import "../../../authPatient/pages/SignupPatient/SignupPatient.css";
import "./SetNewPassword.css";

// doit correspondre à la durée de transition CSS (0.5s)
const SWAP_DURATION = 500;
// clé localStorage partagée entre toutes les pages auth (patient/doctor)
const SWAP_STORAGE_KEY = "authHeroOnLeft";
// layout d'origine de cette page : hero à gauche (classe signup-doctor)
const DEFAULT_HERO_ON_LEFT = true;

const SetNewPasswordDoctor = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const email = location.state?.email || "";
  const resetCode = location.state?.resetCode || "";

  // ── état persistant de la position des cartes (hero à gauche ou à droite) ──
  const initialHeroOnLeft = (() => {
    if (typeof location.state?.heroOnLeft === "boolean") {
      return location.state.heroOnLeft;
    }
    const stored = typeof window !== "undefined" ? localStorage.getItem(SWAP_STORAGE_KEY) : null;
    return stored === null ? DEFAULT_HERO_ON_LEFT : stored === "true";
  })();

  const [heroOnLeft, setHeroOnLeft] = useState(initialHeroOnLeft);

  // ── refs + état pour le swap des 2 cards (form <-> hero) ──
  const heroRef = useRef(null);
  const formWrapperRef = useRef(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStyle, setSwapStyle] = useState({ form: 0, hero: 0 });

  const handleContinueAsPatient = () => {
    if (isSwapping) return;

    const formEl = formWrapperRef.current;
    const heroEl = heroRef.current;

    if (formEl && heroEl) {
      const formRect = formEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const formDeltaX = heroRect.left - formRect.left;
      const heroDeltaX = formRect.left - heroRect.left;
      setSwapStyle({ form: formDeltaX, hero: heroDeltaX });
    }

    setIsSwapping(true);

    const newHeroOnLeft = !heroOnLeft;

    setTimeout(() => {
      localStorage.setItem(SWAP_STORAGE_KEY, String(newHeroOnLeft));
      navigate("/LoginPatient", { state: { heroOnLeft: newHeroOnLeft } });
    }, SWAP_DURATION);
  };

  const handleReset = async () => {
    if (!email || !resetCode) {
      setError("Missing email or reset code. Please restart the reset process.");
      return;
    }
    if (password.length < 6) return setError("Password must be at least 6 characters long");
    if (password !== confirmPassword) return setError("Passwords do not match");

    setError("");
    setLoading(true);

    try {
      await axiosDoctorAuth.post(
        ENDPOINTS.DOCTOR_AUTH.RESET_PASSWORD,
        {
          email,
          code: resetCode,
          newPassword: password,
          confirmNewPassword: confirmPassword,
        },
        { skipAuth: true }
      );

      navigate("/LoginDoctor", { replace: true });
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to reset password";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container signup-doctor-auth">
      <div
        className={`signup-background signup-doctor ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >
        <section
          className="signup-form-wrapper"
          ref={formWrapperRef}
          style={
            isSwapping
              ? { transform: `translateX(${swapStyle.form}px)`, opacity: 0.15 }
              : undefined
          }
        >
          <div className="signup-form-header">
            <h2 className="signup-form-title-main">
              {t("auth.setNewPasswordTitle", "Set a New Password")}
            </h2>
            <p className="signup-form-subtitle">
              {t(
                "auth.setNewPasswordSubtitle",
                "Create a strong password to secure your doctor account."
              )}
            </p>
          </div>

          <div className="signup-form-content">
            <div className="signup-form-group">
              <label className="signup-form-label">
                {t("auth.newPassword", "New Password")}
              </label>
              <Input.Password
                className={`signup-form-input ${error ? "np-input-error" : ""}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("auth.newPassword", "Enter new password")}
              />
            </div>

            <div className="signup-form-group">
              <label className="signup-form-label">
                {t("auth.confirmPassword", "Confirm Password")}
              </label>
              <Input.Password
                className={`signup-form-input ${error ? "np-input-error" : ""}`}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t("auth.confirmPassword", "Confirm password")}
              />
              {error && <span className="np-error-message">{error}</span>}
            </div>

            <Button
              className="signup-create-account-btn"
              onClick={handleReset}
              loading={loading}
              disabled={loading}
              type="primary"
            >
              {t("auth.resetPassword", "Reset Password")}
            </Button>

            <div className="np-back">
              <Button type="link" onClick={() => navigate("/LoginDoctor")} className="np-back-btn">
                <ArrowLeftOutlined /> {t("auth.backToLogin", "Back to Login")}
              </Button>
            </div>
          </div>
        </section>

        <aside
          className="signup-hero"
          ref={heroRef}
          style={{
            backgroundImage: `url(${chooseImage})`,
            ...(isSwapping
              ? { transform: `translateX(${swapStyle.hero}px)`, opacity: 0.15 }
              : {}),
          }}
        >
          <div className="signup-hero-glow" />

          <button
            type="button"
            className="signup-back-home signup-back-home-doctor signup-hero-back-btn"
            onClick={() => navigate("/")}
            aria-label={t("common.backToHome", "Back to home page")}
          >
            <ArrowLeftOutlined />
            <span>{t("common.backToHome", "Back to home page")}</span>
          </button>

          <img
            src={icon1}
            alt=""
            aria-hidden="true"
            className="signup-hero-logo-icon"
          />

          <div className="signup-hero-content">
            <h3 className="signup-hero-title">
              {t("signup.createAccountHeroTitle", "Are you signing up as a patient?")}
            </h3>
            <p className="signup-hero-sub">
              {t(
                "signup.createAccountHeroText",
                "This registration is for doctors only. To book appointments and access care, please create a patient account instead."
              )}
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleContinueAsPatient}
            >
              <span>{t("signup.continueAsPatient", "Continue as a patient")}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default SetNewPasswordDoctor;